"use client";

import { c as _c } from "react/compiler-runtime";
import type { ActivityLogParams } from "@/components/profile-activity/ProfileActivityLogs";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiRepOverview } from "@/generated/models/ApiRepOverview";
import type { ApiRepCategory } from "@/generated/models/ApiRepCategory";
import type { ApiRepCategoriesPage } from "@/generated/models/ApiRepCategoriesPage";
import type { ApiCicOverview } from "@/generated/models/ApiCicOverview";
import { commonApiFetch } from "@/services/api/common-api";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { RateMatter } from "@/types/enums";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import UserPageIdentityHeader from "../identity/header/UserPageIdentityHeader";
import UserPageIdentityHeaderCICRate from "../identity/header/cic-rate/UserPageIdentityHeaderCICRate";
import UserPageIdentityStatements from "../identity/statements/UserPageIdentityStatements";
import UserPageRateWrapper from "../utils/rate/UserPageRateWrapper";
import UserPageCombinedActivityLog from "./UserPageCombinedActivityLog";
import type { RepDirection } from "./UserPageRep.helpers";
import { getCanEditNic } from "./UserPageRep.helpers";
import UserPageRepHeader from "./header/UserPageRepHeader";
import RepContributorsDialog from "./RepContributorsDialog";
import UserPageRepMobile from "./UserPageRepMobile";
const INITIAL_VISIBLE_CATEGORY_COUNT = 5;
const VISIBLE_CATEGORY_LOAD_STEP = 10;
const REP_CATEGORIES_PAGE_SIZE = 25;
const TOP_CONTRIBUTORS_LIMIT = 5;
const FIRST_REP_CATEGORIES_PAGE = 1;
const getDefaultVisibleCounts = (): Record<RepDirection, number> => ({
  received: INITIAL_VISIBLE_CATEGORY_COUNT,
  given: INITIAL_VISIBLE_CATEGORY_COUNT
});
const getDefaultFetchStates = (): Record<RepDirection, boolean> => ({
  received: false,
  given: false
});
type RepContributorsDialogState = {
  readonly scope: "overview";
  readonly direction: RepDirection;
  readonly contributorCount: number;
} | {
  readonly scope: "category";
  readonly direction: RepDirection;
  readonly category: string;
  readonly contributorCount: number;
};
function useRepCategories(t0) {
  const $ = _c(10);
  const {
    user,
    queryDirection,
    apiDirection,
    enabled
  } = t0;
  let t1;
  if ($[0] !== queryDirection || $[1] !== user) {
    t1 = [QueryKey.REP_CATEGORIES, {
      handleOrWallet: user,
      direction: queryDirection
    }];
    $[0] = queryDirection;
    $[1] = user;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== apiDirection || $[4] !== user) {
    t2 = async t3 => {
      const {
        pageParam
      } = t3;
      return await commonApiFetch({
        endpoint: `profiles/${user}/rep/categories`,
        params: {
          ...(apiDirection ? {
            direction: apiDirection
          } : {}),
          page: pageParam.toString(),
          page_size: REP_CATEGORIES_PAGE_SIZE.toString(),
          top_contributors_limit: TOP_CONTRIBUTORS_LIMIT.toString()
        }
      });
    };
    $[3] = apiDirection;
    $[4] = user;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== enabled || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      initialPageParam: FIRST_REP_CATEGORIES_PAGE,
      getNextPageParam: _temp,
      enabled
    };
    $[6] = enabled;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useInfiniteQuery(t3);
}
function _temp(lastPage) {
  return lastPage?.next ? lastPage.page + 1 : undefined;
}
export default function UserPageRep(t0) {
  const $ = _c(133);
  const {
    profile,
    initialActivityLogParams
  } = t0;
  const params = useParams();
  const t1 = params.user as string;
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toLowerCase();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const user = t2;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const [repDirection, setRepDirection] = useState("received");
  const [isNicRateOpen, setIsNicRateOpen] = useState(false);
  const [contributorsDialog, setContributorsDialog] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState(getDefaultVisibleCounts);
  const visibleCountsRef = useRef(visibleCounts);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getDefaultFetchStates();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isFetchingNextPageRef = useRef(t3);
  let t4;
  let t5;
  if ($[3] !== visibleCounts) {
    t4 = () => {
      visibleCountsRef.current = visibleCounts;
    };
    t5 = [visibleCounts];
    $[3] = visibleCounts;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== activeProfileProxy || $[7] !== address || $[8] !== connectedProfile || $[9] !== profile) {
    t6 = getCanEditNic({
      connectedProfile,
      targetProfile: profile,
      activeProfileProxy,
      address
    });
    $[6] = activeProfileProxy;
    $[7] = address;
    $[8] = connectedProfile;
    $[9] = profile;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const canEditNic = t6;
  let t7;
  let t8;
  if ($[11] !== user) {
    t7 = [QueryKey.REP_OVERVIEW, {
      handleOrWallet: user,
      direction: "incoming"
    }];
    t8 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/overview`
    });
    $[11] = user;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  const t9 = !!user;
  let t10;
  if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9
    };
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const {
    data: repOverview,
    isFetching: isFetchingOverview
  } = useQuery(t10);
  const t11 = !!user;
  let t12;
  if ($[18] !== t11 || $[19] !== user) {
    t12 = {
      user,
      queryDirection: "incoming",
      enabled: t11
    };
    $[18] = t11;
    $[19] = user;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const repCategoriesQuery = useRepCategories(t12);
  let t13;
  let t14;
  if ($[21] !== user) {
    t13 = [QueryKey.REP_OVERVIEW, {
      handleOrWallet: user,
      direction: "outgoing"
    }];
    t14 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/overview`,
      params: {
        direction: "outgoing"
      }
    });
    $[21] = user;
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  const t15 = !!user && repDirection === "given";
  let t16;
  if ($[24] !== t13 || $[25] !== t14 || $[26] !== t15) {
    t16 = {
      queryKey: t13,
      queryFn: t14,
      enabled: t15
    };
    $[24] = t13;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const {
    data: repOverviewGiven,
    isFetching: isFetchingOverviewGiven
  } = useQuery(t16);
  const t17 = !!user && repDirection === "given";
  let t18;
  if ($[28] !== t17 || $[29] !== user) {
    t18 = {
      user,
      queryDirection: "outgoing",
      apiDirection: "outgoing",
      enabled: t17
    };
    $[28] = t17;
    $[29] = user;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  const repCategoriesGivenQuery = useRepCategories(t18);
  let t19;
  let t20;
  if ($[31] !== user) {
    t19 = [QueryKey.CIC_OVERVIEW, {
      handleOrWallet: user
    }];
    t20 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/cic/overview`
    });
    $[31] = user;
    $[32] = t19;
    $[33] = t20;
  } else {
    t19 = $[32];
    t20 = $[33];
  }
  const t21 = !!user;
  let t22;
  if ($[34] !== t19 || $[35] !== t20 || $[36] !== t21) {
    t22 = {
      queryKey: t19,
      queryFn: t20,
      enabled: t21
    };
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const {
    data: cicOverview
  } = useQuery(t22);
  let t23;
  if ($[38] !== repCategoriesQuery.data?.pages) {
    t23 = repCategoriesQuery.data?.pages.flatMap(_temp2) ?? [];
    $[38] = repCategoriesQuery.data?.pages;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  const repCategories = t23;
  let t24;
  if ($[40] !== repCategoriesGivenQuery.data?.pages) {
    t24 = repCategoriesGivenQuery.data?.pages.flatMap(_temp3) ?? [];
    $[40] = repCategoriesGivenQuery.data?.pages;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  const repCategoriesGiven = t24;
  const activeOverview = repDirection === "received" ? repOverview ?? null : repOverviewGiven ?? null;
  const activeCategories = repDirection === "received" ? repCategories : repCategoriesGiven;
  const activeHasNextPage = repDirection === "received" ? Boolean(repCategoriesQuery.hasNextPage) : Boolean(repCategoriesGivenQuery.hasNextPage);
  const activeIsFetchingNextPage = repDirection === "received" ? repCategoriesQuery.isFetchingNextPage : repCategoriesGivenQuery.isFetchingNextPage;
  const activeVisibleCount = visibleCounts[repDirection];
  const activeLoading = repDirection === "received" ? isFetchingOverview || repCategoriesQuery.isFetching : isFetchingOverviewGiven || repCategoriesGivenQuery.isFetching;
  let t25;
  if ($[42] !== repCategories.length || $[43] !== repCategoriesGiven.length || $[44] !== repCategoriesGivenQuery.fetchNextPage || $[45] !== repCategoriesGivenQuery.hasNextPage || $[46] !== repCategoriesGivenQuery.isFetchingNextPage || $[47] !== repCategoriesQuery.fetchNextPage || $[48] !== repCategoriesQuery.hasNextPage || $[49] !== repCategoriesQuery.isFetchingNextPage || $[50] !== repDirection) {
    t25 = () => {
      const nextVisibleCounts = {
        ...visibleCountsRef.current,
        [repDirection]: visibleCountsRef.current[repDirection] + VISIBLE_CATEGORY_LOAD_STEP
      };
      visibleCountsRef.current = nextVisibleCounts;
      setVisibleCounts(nextVisibleCounts);
      const loadedCount = repDirection === "received" ? repCategories.length : repCategoriesGiven.length;
      const hasNextPage = repDirection === "received" ? Boolean(repCategoriesQuery.hasNextPage) : Boolean(repCategoriesGivenQuery.hasNextPage);
      const isFetchingNextPage = repDirection === "received" ? repCategoriesQuery.isFetchingNextPage : repCategoriesGivenQuery.isFetchingNextPage;
      const fetchNextPage = repDirection === "received" ? repCategoriesQuery.fetchNextPage : repCategoriesGivenQuery.fetchNextPage;
      if (nextVisibleCounts[repDirection] > loadedCount && hasNextPage && !isFetchingNextPage && !isFetchingNextPageRef.current[repDirection]) {
        isFetchingNextPageRef.current[repDirection] = true;
        fetchNextPage().catch(_temp4).finally(() => {
          isFetchingNextPageRef.current[repDirection] = false;
        });
      }
    };
    $[42] = repCategories.length;
    $[43] = repCategoriesGiven.length;
    $[44] = repCategoriesGivenQuery.fetchNextPage;
    $[45] = repCategoriesGivenQuery.hasNextPage;
    $[46] = repCategoriesGivenQuery.isFetchingNextPage;
    $[47] = repCategoriesQuery.fetchNextPage;
    $[48] = repCategoriesQuery.hasNextPage;
    $[49] = repCategoriesQuery.isFetchingNextPage;
    $[50] = repDirection;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const handleShowMore = t25;
  let t26;
  if ($[52] !== activeOverview || $[53] !== repDirection) {
    t26 = () => {
      if (!activeOverview || activeOverview.contributor_count <= 0) {
        return;
      }
      setContributorsDialog({
        scope: "overview",
        direction: repDirection,
        contributorCount: activeOverview.contributor_count
      });
    };
    $[52] = activeOverview;
    $[53] = repDirection;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  const handleOpenOverviewContributors = t26;
  let t27;
  if ($[55] !== repDirection) {
    t27 = category => {
      if (category.contributor_count <= 0) {
        return;
      }
      setContributorsDialog({
        scope: "category",
        direction: repDirection,
        category: category.category,
        contributorCount: category.contributor_count
      });
    };
    $[55] = repDirection;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  const handleOpenCategoryContributors = t27;
  const t28 = cicOverview ?? null;
  let t29;
  if ($[57] !== activeCategories || $[58] !== activeHasNextPage || $[59] !== activeIsFetchingNextPage || $[60] !== activeLoading || $[61] !== activeOverview || $[62] !== activeVisibleCount || $[63] !== handleOpenCategoryContributors || $[64] !== handleOpenOverviewContributors || $[65] !== handleShowMore || $[66] !== initialActivityLogParams || $[67] !== profile || $[68] !== repDirection || $[69] !== t28) {
    t29 = <div className="lg:tw-hidden"><UserPageRepMobile profile={profile} overview={activeOverview} categories={activeCategories} cicOverview={t28} repDirection={repDirection} onRepDirectionChange={setRepDirection} initialActivityLogParams={initialActivityLogParams} loading={activeLoading} visibleCount={activeVisibleCount} onShowMore={handleShowMore} hasNextPage={activeHasNextPage} isFetchingNextPage={activeIsFetchingNextPage} onOpenOverviewContributors={handleOpenOverviewContributors} onOpenCategoryContributors={handleOpenCategoryContributors} /></div>;
    $[57] = activeCategories;
    $[58] = activeHasNextPage;
    $[59] = activeIsFetchingNextPage;
    $[60] = activeLoading;
    $[61] = activeOverview;
    $[62] = activeVisibleCount;
    $[63] = handleOpenCategoryContributors;
    $[64] = handleOpenOverviewContributors;
    $[65] = handleShowMore;
    $[66] = initialActivityLogParams;
    $[67] = profile;
    $[68] = repDirection;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== activeCategories || $[72] !== activeHasNextPage || $[73] !== activeIsFetchingNextPage || $[74] !== activeLoading || $[75] !== activeOverview || $[76] !== activeVisibleCount || $[77] !== handleOpenCategoryContributors || $[78] !== handleOpenOverviewContributors || $[79] !== handleShowMore || $[80] !== profile || $[81] !== repDirection) {
    t30 = <UserPageRepHeader overview={activeOverview} categories={activeCategories} profile={profile} repDirection={repDirection} onOpenOverviewContributors={handleOpenOverviewContributors} onOpenCategoryContributors={handleOpenCategoryContributors} onRepDirectionChange={setRepDirection} loading={activeLoading} visibleCount={activeVisibleCount} onShowMore={handleShowMore} hasNextPage={activeHasNextPage} isFetchingNextPage={activeIsFetchingNextPage} />;
    $[71] = activeCategories;
    $[72] = activeHasNextPage;
    $[73] = activeIsFetchingNextPage;
    $[74] = activeLoading;
    $[75] = activeOverview;
    $[76] = activeVisibleCount;
    $[77] = handleOpenCategoryContributors;
    $[78] = handleOpenOverviewContributors;
    $[79] = handleShowMore;
    $[80] = profile;
    $[81] = repDirection;
    $[82] = t30;
  } else {
    t30 = $[82];
  }
  let t31;
  if ($[83] !== initialActivityLogParams) {
    t31 = <div className="tw-mt-6 lg:tw-mt-8"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} /></div>;
    $[83] = initialActivityLogParams;
    $[84] = t31;
  } else {
    t31 = $[84];
  }
  let t32;
  if ($[85] !== t30 || $[86] !== t31) {
    t32 = <div className="tw-min-w-0">{t30}{t31}</div>;
    $[85] = t30;
    $[86] = t31;
    $[87] = t32;
  } else {
    t32 = $[87];
  }
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-emerald-500/[0.05] tw-via-transparent tw-to-transparent" />;
    t34 = <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/25 tw-to-transparent" />;
    t35 = <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/40 tw-to-transparent" />;
    t36 = <div className="tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" />;
    t37 = <div className="tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" />;
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
    $[91] = t36;
    $[92] = t37;
  } else {
    t33 = $[88];
    t34 = $[89];
    t35 = $[90];
    t36 = $[91];
    t37 = $[92];
  }
  const t38 = cicOverview ?? null;
  let t39;
  if ($[93] !== canEditNic) {
    t39 = canEditNic ? {
      onRateClick: () => setIsNicRateOpen(true)
    } : {};
    $[93] = canEditNic;
    $[94] = t39;
  } else {
    t39 = $[94];
  }
  let t40;
  if ($[95] !== profile || $[96] !== t38 || $[97] !== t39) {
    t40 = <UserPageIdentityHeader profile={profile} cicOverview={t38} {...t39} />;
    $[95] = profile;
    $[96] = t38;
    $[97] = t39;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== profile) {
    t41 = <UserPageIdentityStatements profile={profile} />;
    $[99] = profile;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t40 || $[102] !== t41) {
    t42 = <div className="tw-min-w-0 tw-self-start"><div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#08090b]">{t33}{t34}{t35}{t36}{t37}<div className="tw-relative tw-z-10">{t40}{t41}</div></div></div>;
    $[101] = t40;
    $[102] = t41;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] !== t32 || $[105] !== t42) {
    t43 = <div className="tw-hidden lg:tw-block"><div className="tw-grid tw-grid-cols-1 tw-gap-x-8 lg:tw-grid-cols-[minmax(0,2fr)_minmax(22rem,1fr)] xl:tw-gap-x-10">{t32}{t42}</div></div>;
    $[104] = t32;
    $[105] = t42;
    $[106] = t43;
  } else {
    t43 = $[106];
  }
  const t44 = contributorsDialog !== null;
  const t45 = contributorsDialog?.scope ?? "overview";
  const t46 = contributorsDialog?.direction ?? repDirection;
  const t47 = contributorsDialog?.scope === "category" ? contributorsDialog.category : null;
  const t48 = contributorsDialog?.contributorCount ?? 0;
  let t49;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = () => setContributorsDialog(null);
    $[107] = t49;
  } else {
    t49 = $[107];
  }
  let t50;
  if ($[108] !== t44 || $[109] !== t45 || $[110] !== t46 || $[111] !== t47 || $[112] !== t48 || $[113] !== user) {
    t50 = <RepContributorsDialog identity={user} isOpen={t44} scope={t45} direction={t46} category={t47} contributorCount={t48} onClose={t49} />;
    $[108] = t44;
    $[109] = t45;
    $[110] = t46;
    $[111] = t47;
    $[112] = t48;
    $[113] = user;
    $[114] = t50;
  } else {
    t50 = $[114];
  }
  let t51;
  if ($[115] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = () => setIsNicRateOpen(false);
    $[115] = t51;
  } else {
    t51 = $[115];
  }
  let t52;
  if ($[116] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = () => setIsNicRateOpen(false);
    $[116] = t52;
  } else {
    t52 = $[116];
  }
  let t53;
  if ($[117] !== profile) {
    t53 = <UserPageIdentityHeaderCICRate profile={profile} isTooltip={false} onSuccess={t52} />;
    $[117] = profile;
    $[118] = t53;
  } else {
    t53 = $[118];
  }
  let t54;
  if ($[119] !== profile || $[120] !== t53) {
    t54 = <UserPageRateWrapper profile={profile} type={RateMatter.NIC}>{t53}</UserPageRateWrapper>;
    $[119] = profile;
    $[120] = t53;
    $[121] = t54;
  } else {
    t54 = $[121];
  }
  let t55;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <div className="tw-mt-3"><button onClick={() => setIsNicRateOpen(false)} type="button" className="tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button></div>;
    $[122] = t55;
  } else {
    t55 = $[122];
  }
  let t56;
  if ($[123] !== t54) {
    t56 = <div className="tw-px-4 sm:tw-px-6">{t54}{t55}</div>;
    $[123] = t54;
    $[124] = t56;
  } else {
    t56 = $[124];
  }
  let t57;
  if ($[125] !== isNicRateOpen || $[126] !== t56) {
    t57 = <MobileWrapperDialog title="Rate NIC" isOpen={isNicRateOpen} onClose={t51} tabletModal={true} maxWidthClass="md:tw-max-w-md">{t56}</MobileWrapperDialog>;
    $[125] = isNicRateOpen;
    $[126] = t56;
    $[127] = t57;
  } else {
    t57 = $[127];
  }
  let t58;
  if ($[128] !== t29 || $[129] !== t43 || $[130] !== t50 || $[131] !== t57) {
    t58 = <div className="tailwind-scope">{t29}{t43}{t50}{t57}</div>;
    $[128] = t29;
    $[129] = t43;
    $[130] = t50;
    $[131] = t57;
    $[132] = t58;
  } else {
    t58 = $[132];
  }
  return t58;
}
function _temp4() {}
function _temp3(page_0) {
  return page_0.data;
}
function _temp2(page) {
  return page.data;
}